import { toast } from 'sonner';
import { BookmarkIcon, ShareIcon } from './icons';
import { ACTIVITY_DETAILS_COLORS, ACTIVITY_DETAILS_RADIUS, ACTIVITY_DETAILS_RESPONSIVE, ACTIVITY_DETAILS_TYPOGRAPHY } from './constants';
import { useCreateBookmark } from '../../hooks/useBookmarks';

interface ActivityActionButtonsProps {
  activityId: number;
  isBookmarked: boolean;
  onShare?: () => void;
  onBookmarkChange?: (isBookmarked: boolean) => void;
}

/**
 * Action buttons for activity details page
 * Handles save for later and share functionality
 */
export default function ActivityActionButtons({
  activityId,
  isBookmarked,
  onShare,
  onBookmarkChange,
}: ActivityActionButtonsProps) {
  const createBookmark = useCreateBookmark();

  const handleSaveForLater = async () => {
    try {
      await createBookmark.mutateAsync(activityId);
      toast.success('Activity saved!', {
        description: 'You can find it in your Activity Library',
        duration: 3000,
      });
      onBookmarkChange?.(true);
    } catch (error) {
      console.error('Failed to save activity:', error);
      toast.error('Failed to save activity', {
        description: error instanceof Error ? error.message : 'Please try again',
        duration: 4000,
      });
    }
  };

  return (
    <div 
      className={`flex gap-2 mt-4 px-6 sm:px-8 md:px-10 lg:px-12`}
    >
      {/* Save for Later - Secondary Button */}
      <button
        onClick={handleSaveForLater}
        disabled={isBookmarked || createBookmark.isPending}
        className={`flex-1 flex items-center justify-center gap-2.5 ${ACTIVITY_DETAILS_RESPONSIVE.buttonHeightClasses}
          transition-all duration-200 ease-in-out
          ${
            isBookmarked
              ? 'cursor-default'
              : 'hover:bg-gray-50 active:bg-gray-100'
          }
          ${createBookmark.isPending ? 'opacity-50 cursor-wait' : ''}
          disabled:cursor-not-allowed
        `}
        style={{
          backgroundColor: isBookmarked ? '#E8F5E9' : ACTIVITY_DETAILS_COLORS.white,
          border: isBookmarked ? `2px solid ${ACTIVITY_DETAILS_COLORS.background}` : `2px solid ${ACTIVITY_DETAILS_COLORS.gray6}`,
          borderRadius: `${ACTIVITY_DETAILS_RADIUS.button}px`,
          cursor: isBookmarked ? 'default' : 'pointer',
        }}
      >
        {createBookmark.isPending ? (
          <>
            <div className="animate-spin rounded-full h-5 w-5 border-2 border-gray-300 border-t-gray-600" />
            <span
              className={`${ACTIVITY_DETAILS_TYPOGRAPHY.button.weight} ${ACTIVITY_DETAILS_TYPOGRAPHY.button.transform} ${ACTIVITY_DETAILS_TYPOGRAPHY.button.sizeClasses} ${ACTIVITY_DETAILS_TYPOGRAPHY.button.tracking}`}
              style={{
                fontFamily: 'DM Sans, sans-serif',
                color: ACTIVITY_DETAILS_COLORS.gray6,
              }}
            >
              Saving...
            </span>
          </>
        ) : (
          <>
            <BookmarkIcon
              size={24}
              filled={isBookmarked}
              color={isBookmarked ? ACTIVITY_DETAILS_COLORS.background : ACTIVITY_DETAILS_COLORS.gray6}
            />
            <span
              className={`${ACTIVITY_DETAILS_TYPOGRAPHY.button.weight} ${ACTIVITY_DETAILS_TYPOGRAPHY.button.transform} ${ACTIVITY_DETAILS_TYPOGRAPHY.button.sizeClasses} ${ACTIVITY_DETAILS_TYPOGRAPHY.button.tracking}`}
              style={{
                fontFamily: 'DM Sans, sans-serif',
                color: isBookmarked ? ACTIVITY_DETAILS_COLORS.background : ACTIVITY_DETAILS_COLORS.gray6,
              }}
            >
              {isBookmarked ? 'Saved' : 'Save for later'}
            </span>
          </>
        )}
      </button>

      {/* Share Activity - Primary Button */}
      <button
        onClick={onShare}
        className={`flex-1 flex items-center justify-center gap-2.5 ${ACTIVITY_DETAILS_RESPONSIVE.buttonHeightClasses}`}
        style={{
          backgroundColor: ACTIVITY_DETAILS_COLORS.gray6,
          border: 'none',
          borderRadius: `${ACTIVITY_DETAILS_RADIUS.button}px`,
          cursor: 'pointer',
        }}
      >
        <ShareIcon size={24} color={ACTIVITY_DETAILS_COLORS.white} />
        <span
          className={`${ACTIVITY_DETAILS_TYPOGRAPHY.button.weight} ${ACTIVITY_DETAILS_TYPOGRAPHY.button.transform} ${ACTIVITY_DETAILS_TYPOGRAPHY.button.sizeClasses} ${ACTIVITY_DETAILS_TYPOGRAPHY.button.tracking}`}
          style={{
            fontFamily: 'DM Sans, sans-serif',
            color: ACTIVITY_DETAILS_COLORS.white,
          }}
        >
          Share Activity
        </span>
      </button>
    </div>
  );
}
